import * as _ from "lodash"
import { IHierarchyStructure, IHierarchyStructureNavigator } from '../PA.MD.Core/PA.MD.IF.Hierarchy'
import { GenericLease } from '../PA.Utils/GenericLease'
import * as AsyncLock from 'async-lock';
import { HierarchyStructureManager } from './HierarchyStructureManager'
import { LeaseRegistry } from '../PA.Utils/LeaseRegistry'
import { PeriodTypeService } from '../PA.MD.Services/PeriodTypeService'
import { PeriodService } from '../PA.MD.Services/PeriodService'
import { DateUtils } from '../PA.Core/Basic/Dates'
import { HierarchyStructureNavigator } from './HierarchyStructureNavigator'


interface IHierarchyStructureCacheEntry {
    wbHierarchyId: number
    HierarchyStructure: IHierarchyStructure
    Lease: GenericLease
}

interface IHierarchyStructureNavigatorCacheEntry {
    wbHierarchyId: number
    DateRangeKey: string
    StartDt: Date
    EndDt: Date

    HierarchyStructureNavigator: IHierarchyStructureNavigator
    Lease: GenericLease
}


export class HierarchyStructureClient
{
    private static cacheLock:AsyncLock = new AsyncLock({})
    private static dctHS: {[wbHierarchyId: number]: IHierarchyStructureCacheEntry} = {}
    private static dctHSN: {[wbHierarchyId: number]: {[dateRangeKey: string]: IHierarchyStructureNavigatorCacheEntry}} = {}


    static async getHierarchy(wbHierarchyId: number): Promise<IHierarchyStructure> {
        if(!_.isNumber(+wbHierarchyId))
            return undefined

        let dhs = HierarchyStructureClient.dctHS

        return await HierarchyStructureClient.cacheLock.acquire<IHierarchyStructure>("HierarchyStructure", async (done) => {
            if(wbHierarchyId in dhs){
                let hse = dhs[wbHierarchyId]
                await HS_checkLeaseReload(hse)
                done(undefined, hse.HierarchyStructure)
            } else {
                // Create Cache Entry
                let hs = await HierarchyStructureManager.getHierarchyStructure(+wbHierarchyId)
                await hs.OnLoadComplete()
                let hse: IHierarchyStructureCacheEntry = {
                    wbHierarchyId: +wbHierarchyId,
                    Lease: undefined,
                    HierarchyStructure: hs
                }

                let leaseType = LeaseRegistry.Lease_Subject_Product_Hierarchy
                let leaseClasses: string[] = []
                switch(hs.entityDimension){

                    case "Customer":
                        leaseType = LeaseRegistry.Lease_Subject_Customer_Hierarchy
                        leaseClasses = [LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Customer_Hierarchy, hs.wbId)]
                        break

                    case "Product":
                        leaseType = LeaseRegistry.Lease_Subject_Product_Hierarchy
                        leaseClasses = [LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Product_Hierarchy, hs.wbId)]
                        break

                    case "Period":
                        leaseType = LeaseRegistry.Lease_Subject_Period_Hierarchy
                        leaseClasses = [LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Period_Hierarchy, hs.wbId)]
                        break

                }
                hse.Lease = new GenericLease(leaseType)
                hse.Lease.addClasses(leaseClasses)
                dhs[wbHierarchyId] = hse
                done(undefined, hse.HierarchyStructure)
            }
        })

    }

    static async getHierarchyByDimensionWbId(EntityDimension: string, wbTargetWbId: number): Promise<IHierarchyStructure> {
        let wbHierarchyId: number = await HierarchyStructureManager.getWbHierarchyId(EntityDimension, wbTargetWbId)
        return HierarchyStructureClient.getHierarchy(wbHierarchyId)
    }

    // Hierarchy Structure Navigation
    static async getHierarchyNavigatorRange(wbHierarchyId: number, startDate: Date, endDate: Date): Promise<IHierarchyStructureNavigator> {
        if(!_.isNumber(+wbHierarchyId))
            return undefined

        let periodKey = `${DateUtils.convertToSAPBW(startDate)}-${DateUtils.convertToSAPBW(endDate)}`

        let dhsn = HierarchyStructureClient.dctHSN

        return await HierarchyStructureClient.cacheLock.acquire<IHierarchyStructureNavigator>("HierarchyStructureNavigator", async (done) => {

            if(!(wbHierarchyId in dhsn))
                dhsn[wbHierarchyId] = {}
            let pc = dhsn[wbHierarchyId]

            if(periodKey in pc){
                let hsne = pc[periodKey]
                await HSN_checkLeaseReload(hsne)
                done(undefined, hsne.HierarchyStructureNavigator)
            } else {

                // Create Cache Entry
                let hs = await HierarchyStructureClient.getHierarchy(+wbHierarchyId)
                let hsn = HierarchyStructureNavigator.getHierarchyStructureNavigator(hs, startDate, endDate)
                await hsn.onLoadComplete()

                let hsne: IHierarchyStructureNavigatorCacheEntry = {
                    wbHierarchyId: +wbHierarchyId,
                    StartDt: startDate,
                    EndDt: endDate,
                    DateRangeKey: periodKey,

                    Lease: undefined,
                    HierarchyStructureNavigator: hsn,
                }

                let leaseType = LeaseRegistry.Lease_Subject_Product_Hierarchy
                let leaseClasses: string[] = []
                switch(hs.entityDimension){

                    case "Customer":
                        leaseType = LeaseRegistry.Lease_Subject_Customer_Hierarchy
                        leaseClasses = [LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Customer_Hierarchy, hs.wbId)]
                        break

                    case "Product":
                        leaseType = LeaseRegistry.Lease_Subject_Product_Hierarchy
                        leaseClasses = [LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Product_Hierarchy, hs.wbId)]
                        break

                    case "Period":
                        leaseType = LeaseRegistry.Lease_Subject_Period_Hierarchy
                        leaseClasses = [LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Period_Hierarchy, hs.wbId)]
                        break

                }
                hsne.Lease = new GenericLease(leaseType)
                hsne.Lease.addClasses(leaseClasses)
                pc[periodKey] = hsne
                done(undefined, hsne.HierarchyStructureNavigator)
            }
        })
    }

    static async getHierarchyNavigatorPeriod(wbHierarchyId: number, wbPeriodId: number): Promise<IHierarchyStructureNavigator> {
        let p = await PeriodService.getPeriodByWBID(wbPeriodId)
        return await HierarchyStructureClient.getHierarchyNavigatorRange(wbHierarchyId, p.PeriodStartDt, p.PeriodEndDt)
    }

    static async getHierarchyNavigatorRangeByDimensionWbId(EntityDimension: string, wbTargetWbId: number, startDate: Date, endDate: Date): Promise<IHierarchyStructureNavigator> {
        let wbHierarchyId: number = await HierarchyStructureManager.getWbHierarchyId(EntityDimension, wbTargetWbId)
        return await HierarchyStructureClient.getHierarchyNavigatorRange(wbHierarchyId, startDate, endDate)
    }

    static async getHierarchyNavigatorPeriodByDimensionWbId(EntityDimension: string, wbTargetWbId: number, wbPeriodId: number): Promise<IHierarchyStructureNavigator> {
        let wbHierarchyId: number = await HierarchyStructureManager.getWbHierarchyId(EntityDimension, wbTargetWbId)
        return await HierarchyStructureClient.getHierarchyNavigatorPeriod(wbHierarchyId, wbPeriodId)
    }



}
// Reloaded
async function HS_checkLeaseReload(hsce: IHierarchyStructureCacheEntry): Promise<boolean> {
    if(await hsce.Lease.hasDataChanged()){
        hsce.Lease.resetLease()
        // Reload
        let hs = await HierarchyStructureManager.getHierarchyStructure(hsce.wbHierarchyId)
        await hs.OnLoadComplete()
        hsce.HierarchyStructure = hs
        return true
    }
    return false

}

async function HSN_checkLeaseReload(hsnce: IHierarchyStructureNavigatorCacheEntry): Promise<boolean> {
    if(await hsnce.Lease.hasDataChanged()){
        hsnce.Lease.resetLease()
        // Reload
        let hs = await HierarchyStructureClient.getHierarchy(hsnce.wbHierarchyId)
        let hsn = HierarchyStructureNavigator.getHierarchyStructureNavigator(hs, hsnce.StartDt, hsnce.EndDt)
        await hsn.onLoadComplete()
        hsnce.HierarchyStructureNavigator = hsn
        return true
    }

    return false
}
